// Sesión, empresa activa y permisos efectivos. La empresa activa es SIEMPRE explícita: nada se consulta
// ni se opera sin ella, y cambiarla borra todo lo que se leyó de la anterior (claves ['emp', id, …]).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, ErrorApi, registrarSesionExpirada } from '../api/cliente';
import type { Empresa, PermisosEfectivos, SesionIniciada, Yo } from '../api/contratos';

const CLAVE_EMPRESA = 'madeci.empresaActiva';

export type EstadoSesion = 'verificando' | 'anonimo' | 'autenticado';

export interface Sesion {
  estado: EstadoSesion;
  usuario: Yo | null;
  /** Hubo un 401 en plena operación: se pide la clave de nuevo sin perder lo abierto. */
  expirada: boolean;
  empresas: Empresa[];
  empresa: Empresa | null;
  permisos: ReadonlySet<string>;
  permisosCargados: boolean;
  /** No se pudo verificar la sesión por falta de red o servidor caído (no es «sin sesión»). */
  sinConexion: boolean;
  puede(permiso: string): boolean;
  iniciar(correo: string, clave: string): Promise<void>;
  cerrar(): Promise<void>;
  elegirEmpresa(id: string): void;
  /** Roles del usuario en la empresa activa (solo para mostrar). */
  roles: string[];
}

const Ctx = createContext<Sesion | null>(null);

function leerEmpresaGuardada(): string | null {
  try {
    return sessionStorage.getItem(CLAVE_EMPRESA);
  } catch {
    return null;
  }
}
function guardarEmpresa(id: string | null): void {
  try {
    if (id) sessionStorage.setItem(CLAVE_EMPRESA, id);
    else sessionStorage.removeItem(CLAVE_EMPRESA);
  } catch {
    /* sin almacenamiento: la empresa se vuelve a elegir al recargar */
  }
}

export function ProveedorSesion({
  children,
  alCambiarEmpresa,
}: {
  children: ReactNode;
  /** El shell cierra las pestañas: su estado pertenece a la empresa anterior. */
  alCambiarEmpresa?: () => void;
}) {
  const qc = useQueryClient();
  const [expirada, setExpirada] = useState(false);
  const [empresaId, setEmpresaId] = useState<string | null>(leerEmpresaGuardada);

  const yo = useQuery({
    queryKey: ['sesion', 'yo'],
    queryFn: () => api.get<Yo>('/auth/yo', { silenciar401: true }),
    retry: false,
    staleTime: Infinity,
  });
  const autenticado = yo.isSuccess;
  const estado: EstadoSesion = yo.isPending
    ? 'verificando'
    : autenticado
      ? 'autenticado'
      : 'anonimo';

  const empresas = useQuery({
    queryKey: ['sesion', 'empresas'],
    queryFn: () => api.get<Empresa[]>('/empresas'),
    enabled: autenticado,
    staleTime: 5 * 60_000,
  });
  const lista = useMemo(() => (empresas.data ?? []).filter((e) => e.activa), [empresas.data]);
  // Una sola empresa: se elige sola. Una guardada que ya no es alcanzable: se descarta.
  useEffect(() => {
    if (!empresas.isSuccess) return;
    if (empresaId && !lista.some((e) => e.id === empresaId)) {
      setEmpresaId(null);
      guardarEmpresa(null);
    } else if (!empresaId && lista.length === 1) {
      setEmpresaId(lista[0]!.id);
      guardarEmpresa(lista[0]!.id);
    }
  }, [empresas.isSuccess, lista, empresaId]);
  const empresa = lista.find((e) => e.id === empresaId) ?? null;

  const permisos = useQuery({
    queryKey: ['emp', empresa?.id, 'permisos'],
    queryFn: () =>
      api.get<PermisosEfectivos>('/auth/permisos', { query: { empresaId: empresa!.id } }),
    enabled: autenticado && !!empresa,
    staleTime: 60_000,
  });
  const conjunto = useMemo(() => new Set(permisos.data?.permisos ?? []), [permisos.data]);

  useEffect(() => {
    registrarSesionExpirada(() => setExpirada(true));
    return () => registrarSesionExpirada(null);
  }, []);

  const iniciar = useCallback(
    async (correo: string, clave: string) => {
      // Desde una app instalada en el celular (/app/ruta/, /app/bodega/) la sesión dura y se renueva.
      const origen = window.location.pathname.startsWith('/app/') ? 'movil' : 'web';
      await api.post<SesionIniciada>(
        '/auth/sesion',
        { correo, clave, origen },
        { silenciar401: true },
      );
      setExpirada(false);
      // Todo lo sensible a la identidad se vuelve a leer (otro usuario = otros permisos).
      await qc.invalidateQueries({ queryKey: ['sesion'] });
      await qc.invalidateQueries({ queryKey: ['emp'] });
    },
    [qc],
  );

  const cerrar = useCallback(async () => {
    try {
      await api.delete('/auth/sesion', { silenciar401: true });
    } catch (e) {
      if (!(e instanceof ErrorApi)) throw e;
    }
    guardarEmpresa(null);
    setEmpresaId(null);
    setExpirada(false);
    alCambiarEmpresa?.();
    qc.clear();
  }, [qc, alCambiarEmpresa]);

  const elegirEmpresa = useCallback(
    (id: string) => {
      if (id === empresaId) return;
      // Nada de la empresa anterior sobrevive: consultas en curso, cachés, pestañas y selección.
      void qc.cancelQueries({ queryKey: ['emp'] });
      qc.removeQueries({ queryKey: ['emp'] });
      alCambiarEmpresa?.();
      guardarEmpresa(id);
      setEmpresaId(id);
    },
    [qc, empresaId, alCambiarEmpresa],
  );

  const roles = useMemo(
    () =>
      (yo.data?.asignaciones ?? [])
        .filter((a) => a.empresa_id === null || a.empresa_id === empresa?.id)
        .map((a) => a.rol),
    [yo.data, empresa?.id],
  );

  const valor: Sesion = {
    estado,
    usuario: yo.data ?? null,
    expirada,
    empresas: lista,
    empresa,
    permisos: conjunto,
    permisosCargados: permisos.isSuccess,
    sinConexion:
      yo.isError &&
      (!(yo.error instanceof ErrorApi) || yo.error.status === 0 || yo.error.status >= 500),
    // «a|b»: basta con uno de los permisos (p. ej. las excepciones de caja las ven el nivel 1, la
    // revisora alterna y el nivel 2).
    puede: (p) => p.split('|').some((x) => conjunto.has(x)),
    iniciar,
    cerrar,
    elegirEmpresa,
    roles,
  };
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSesion(): Sesion {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSesion fuera de ProveedorSesion');
  return s;
}

/** La empresa activa, garantizada (solo dentro del shell autenticado). */
export function useEmpresa(): Empresa {
  const { empresa } = useSesion();
  if (!empresa) throw new Error('No hay empresa activa');
  return empresa;
}
